import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import { selectAssignedProvidersCount } from '@provider-portal/common/experiments/account';
import {
  MenuEditorParams,
  shouldEnableMenuEditor,
  SSU_EXPERIMENT_EVENTS,
  useMenuEditorExperimentEnabled,
} from '@provider-portal/common/experiments/providerOnboarding';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { VendorAssignedProvider } from '@provider-portal/common/services/types/account';
import { StoreListGroup } from '@provider-portal/mp2-common/components/StoreSelector/StoreSelectorStoresList';
import { getFilteredTabHeaderForRoute } from 'root/navigation/navStructureUtils';
import {
  selectAssignedProvidersByVendor,
  selectSelectedProvider,
  selectSelectedProviderPermissions,
} from 'rdx/account';

import { NavBarMenuItem } from './NavBarMenu';
import { getModuleNameFromMenuCategoryList } from './utils';
import { selectTargetingParameters } from 'rdx/targetingParameters/selectors';

interface ModuleInfo {
  moduleName: string | null;
  tabName: string | null;
  tabRank: number | null;
  module: ReturnType<typeof getFilteredTabHeaderForRoute>;
}

/**
 * Hook to track menu editor metadata for analytics
 */
export function useTrackMenuEditorMetadata(menuEditorParams: MenuEditorParams): void {
  const { mixpanelService } = useCommonAppServices();
  const selectedProvider = useSelector(selectSelectedProvider);
  const accountProvidersCount = useSelector(selectAssignedProvidersCount);
  const isMenuEditorExperimentEnabled = useMenuEditorExperimentEnabled();

  useEffect(() => {
    if (!isMenuEditorExperimentEnabled) {
      return;
    }

    mixpanelService.trackProviderEvent(SSU_EXPERIMENT_EVENTS.LOADED, {
      providerId: selectedProvider.providerId,
      isDesktop: menuEditorParams.isDesktop,
      isTreatment: menuEditorParams.isWithMenuEditor,
      isOnboarding: menuEditorParams.isAccountOnboarding,
      hasMenuEditor: shouldEnableMenuEditor(menuEditorParams),
      providerSignupSource: selectedProvider.signup_source,
      accountProvidersCount,
    });
  }, [
    mixpanelService,
    menuEditorParams,
    accountProvidersCount,
    selectedProvider.providerId,
    selectedProvider.signup_source,
    isMenuEditorExperimentEnabled,
  ]);
}

/**
 * Hook to get module information for a given route
 */
export function useGetModuleInfo(menuCategoryList: NavBarMenuItem[]) {
  const providerPermissions = useSelector(selectSelectedProviderPermissions, isEqual);
  const targetingParameters = useSelector(selectTargetingParameters);

  return useCallback(
    (route: string): ModuleInfo => {
      const module = getFilteredTabHeaderForRoute(route, providerPermissions, targetingParameters);

      if (!module) {
        return {
          moduleName: getModuleNameFromMenuCategoryList(menuCategoryList, route),
          tabName: null,
          tabRank: null,
          module: null,
        };
      }

      const tab = module.tabs.find((t) => t.route === route);
      const tabRank = tab ? module.tabs.indexOf(tab) + 1 : null;

      return {
        moduleName: module.id,
        tabName: tab?.id ?? null,
        tabRank,
        module,
      };
    },
    [menuCategoryList, providerPermissions, targetingParameters],
  );
}

/**
 * Hook to build venue selector configuration
 */
export function useVenueSelectorConfig(
  isProviderInSSUFlow: boolean,
  onProviderSelect: (storeId: number) => void,
  onAddVenue: () => void,
) {
  const selectedProvider = useSelector(selectSelectedProvider);
  const assignedProvidersByVendor: Map<number, VendorAssignedProvider> | undefined = useSelector(
    selectAssignedProvidersByVendor,
  );

  return useMemo(() => {
    const stores: StoreListGroup[] = [];

    for (const vendorProviders of assignedProvidersByVendor.values()) {
      if (vendorProviders.providers.length > 0) {
        stores.push({
          parentName: vendorProviders.vendor_name,
          stores: vendorProviders.providers.map((provider) => ({
            storeName: provider.provider_name,
            storeId: provider.provider_id,
          })),
        });
      }
    }

    const showAddVenueButton = !isProviderInSSUFlow;

    return {
      parentName: selectedProvider.vendorName || '',
      storeName: selectedProvider.providerName || '',
      selectedStoreId: selectedProvider.providerId,
      stores,
      onProviderSelect,
      showAddVenueButton,
      onAddVenue,
    };
  }, [
    assignedProvidersByVendor,
    isProviderInSSUFlow,
    onProviderSelect,
    selectedProvider.providerId,
    selectedProvider.providerName,
    selectedProvider.vendorName,
    onAddVenue,
  ]);
}
